import { useSelector } from 'react-redux';
import { ListItemIcon, Typography } from '@mui/material';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import AddToLibraryIcon from 'assets/icons/AddToLibraryIcon';
import { selectEmailConfirmed } from 'services/store/user/selectors';
import { useAuthStore, useEmailConfirmationDialog } from 'services/hooks';
import { StyledMenuItem } from './styled';

type PropsType = {
  isInLibrary: boolean;
  removeFromLibrary: () => void;
  addToLibrary: () => void;
};

const AddToLibraryButton = ({
  isInLibrary,
  removeFromLibrary,
  addToLibrary,
}: PropsType) => {
  const { t } = useTranslation();

  const { authStore } = useAuthStore();
  const { isLoggedIn } = authStore;

  const isEmailConfirmed = useSelector(selectEmailConfirmed);
  const {
    onEmailConfirmationDialogShouldOpen,
    displayEmailConfirmationDialog,
  } = useEmailConfirmationDialog();

  const addToLibraryTitle = useMemo(() => {
    if (isInLibrary) {
      return t('songList:actions:deleteSong');
    }
    return t('songList:actions:addToLibrary');
  }, [t, isInLibrary]);

  const shouldDisplayEmailConfirmationDialog = useMemo(() => {
    if (!isLoggedIn) {
      return false;
    }
    return !isEmailConfirmed;
  }, [isLoggedIn, isEmailConfirmed]);

  const handleAddToLibraryClick = useCallback(() => {
    if (shouldDisplayEmailConfirmationDialog) {
      onEmailConfirmationDialogShouldOpen();
      return;
    }

    if (isInLibrary) {
      removeFromLibrary();
      return;
    }
    addToLibrary();
  }, [
    shouldDisplayEmailConfirmationDialog,
    isInLibrary,
    removeFromLibrary,
    onEmailConfirmationDialogShouldOpen,
    addToLibrary,
  ]);

  return (
    <>
      <StyledMenuItem onClick={handleAddToLibraryClick}>
        <ListItemIcon>
          <AddToLibraryIcon isAdded={isInLibrary} />
        </ListItemIcon>
        <Typography>{addToLibraryTitle}</Typography>
      </StyledMenuItem>
      {displayEmailConfirmationDialog()}
    </>
  );
};

export default AddToLibraryButton;
